(() => {
  'use strict';

  if (window.FFRemakeMiniOptions?.version === 2) return;

  const BLOCK = '[data-remake-mini]';
  const engravingPattern = /^[A-Za-z.]{1,10}$/;
  const expectedPrice = 400000;
  const busyForms = new WeakSet();
  const initializedBlocks = new WeakSet();

  function getBlocks(form) {
    return Array.from(document.querySelectorAll(BLOCK)).filter(
      (block) => block.dataset.formId === form.getAttribute('id')
    );
  }

  function isMiniSelected(block) {
    const select = block.querySelector('[data-remake-mini-select]');
    return Boolean(
      select?.selectedOptions[0]?.hasAttribute('data-remake-mini-choice')
    );
  }

  function addonReady(block) {
    return (
      /^\d+$/.test(block.dataset.addonVariantId || '') &&
      Number(block.dataset.addonPrice) === expectedPrice &&
      block.dataset.addonAvailable === 'true'
    );
  }

  function validateEngraving(input) {
    input.setCustomValidity('');

    if (input.disabled) return;

    if (!input.value) {
      input.setCustomValidity('刻印する文字を入力してください。');
    } else if (!engravingPattern.test(input.value)) {
      input.setCustomValidity(
        '半角アルファベットの大文字・小文字とドットのみ、' +
        '1〜10文字で入力してください。スペースは使えません。'
      );
    }
  }

  function updateBlock(block) {
    const select = block.querySelector('[data-remake-mini-select]');
    const choice = block.querySelector('[data-remake-mini-choice]');
    const engraving = block.querySelector('[data-remake-mini-engraving]');
    const input = block.querySelector('[data-remake-mini-input]');

    if (!select || !choice || !engraving || !input) return;

    choice.disabled = !addonReady(block);

    select.setCustomValidity(
      isMiniSelected(block) && !addonReady(block)
        ? 'ミニミニランドセルは現在選択できません。'
        : ''
    );

    const selected = isMiniSelected(block);
    engraving.hidden = !selected;
    input.disabled = !selected;
    input.required = selected;

    if (!selected) input.value = '';

    validateEngraving(input);

    const status = block.querySelector('[data-remake-mini-status]');
    if (status) {
      status.hidden = !selected;
      status.textContent = selected
        ? 'この小物の追加料金：4,000円（税込・1点につき）'
        : '';
    }
  }

  function initialize(root = document) {
    const blocks = Array.from(root.querySelectorAll(BLOCK));
    if (root instanceof Element && root.matches(BLOCK)) {
      blocks.unshift(root);
    }

    blocks.forEach((block) => {
      if (initializedBlocks.has(block)) return;

      const select = block.querySelector('[data-remake-mini-select]');
      const input = block.querySelector('[data-remake-mini-input]');
      if (!select || !input) return;

      initializedBlocks.add(block);

      select.addEventListener('change', () => updateBlock(block));
      input.addEventListener('input', () => validateEngraving(input));

      updateBlock(block);
    });
  }

  function showError(form, message) {
    const block = getBlocks(form)[0];
    if (!block) return;

    let error = block.querySelector('[data-remake-mini-error]');

    if (!error) {
      error = document.createElement('p');
      error.dataset.remakeMiniError = '';
      error.setAttribute('role', 'alert');
      error.style.color = '#b42318';
      error.style.marginTop = '12px';
      block.appendChild(error);
    }

    error.textContent = message;
    error.hidden = false;
  }

  function clearError(form) {
    getBlocks(form).forEach((block) => {
      const error = block.querySelector('[data-remake-mini-error]');
      if (error) error.hidden = true;
    });
  }

  function stopEvent(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function cartRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  async function addWithOptions(form, blocks) {
    if (busyForms.has(form)) return;

    clearError(form);

    const selectedBlocks = blocks.filter(isMiniSelected);

    if (selectedBlocks.some((block) => !addonReady(block))) {
      showError(form, '追加料金用の商品設定を確認してください。');
      return;
    }

    const data = new FormData(form);
    const mainId = String(data.get('id') || '');
    const quantity = Number(data.get('quantity') || 1);

    if (
      !/^\d+$/.test(mainId) ||
      !Number.isSafeInteger(quantity) ||
      quantity < 1
    ) {
      showError(form, '商品と数量を確認してください。');
      return;
    }

    // 既存の有料オプションとの併用は、未検証のため送信しない
    const otherPaidOption = Array.from(form.elements).some((element) => {
      return (
        element instanceof HTMLSelectElement &&
        element.closest('.option__target') &&
        element.selectedOptions[0]?.getAttribute('data-id')
      );
    });

    if (otherPaidOption) {
      showError(
        form,
        '他の有料オプションとの同時注文には対応していません。店舗へお問い合わせください。'
      );
      return;
    }

    const properties = {};

    for (const [name, value] of data.entries()) {
      const match = name.match(/^properties\[(.+)\]$/);
      if (!match) continue;

      if (typeof value !== 'string') {
        showError(form, 'ファイル添付との同時注文には対応していません。');
        return;
      }

      if (value !== '') properties[match[1]] = value;
    }

    const groupId =
      window.crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    properties._remake_group = groupId;

    const mainItem = {
      id: mainId,
      quantity,
      properties
    };

    const sellingPlan = data.get('selling_plan');
    if (sellingPlan) mainItem.selling_plan = String(sellingPlan);

    const items = [mainItem];

    selectedBlocks.forEach((block) => {
      const select = block.querySelector('[data-remake-mini-select]');
      const input = block.querySelector('[data-remake-mini-input]');
      const slot =
        select.name.match(/^properties\[(.+)\]$/)?.[1] || '小物A';

      items.push({
        id: block.dataset.addonVariantId,
        quantity,
        properties: {
          '選択箇所': slot,
          '刻印文字': input.value,
          '_remake_group': groupId,
          '_remake_parent_variant': mainId
        }
      });
    });

    busyForms.add(form);

    const buttons = Array.from(form.elements).filter(
      (element) =>
        (element instanceof HTMLButtonElement ||
          element instanceof HTMLInputElement) &&
        element.type === 'submit'
    );

    const buttonStates = buttons.map((button) => ({
      button,
      disabled: button.disabled
    }));

    buttons.forEach((button) => {
      button.disabled = true;
    });

    let requestStarted = false;

    try {
      const requestBody = JSON.stringify({ items });
      requestStarted = true;

      const response = await fetch(`${cartRoot()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: requestBody
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error('カート追加に失敗しました。');
      }

      // 追加料金を含む内容を、カート画面で確認する
      window.location.assign(`${cartRoot()}cart`);
    } catch (error) {
      if (requestStarted) {
        // 通信切断時などは追加済みか断定できないため、
        // 自動再送せず、カートを確認できる状態にする
        showError(
          form,
          '追加結果を確認できませんでした。二重追加を避けるため、カートの内容を確認してください。'
        );

        const block = blocks[0];
        if (!block.querySelector('[data-remake-cart-link]')) {
          const link = document.createElement('a');
          link.dataset.remakeCartLink = '';
          link.href = `${cartRoot()}cart`;
          link.textContent = 'カートを確認する';
          block.appendChild(link);
        }

        return;
      }

      showError(form, '処理を開始できませんでした。');
      busyForms.delete(form);
      buttonStates.forEach(({ button, disabled }) => {
        button.disabled = disabled;
      });
    }
  }

  function handlePurchase(event, form) {
    if (!form || !form.id) return;

    initialize();

    const blocks = getBlocks(form);
    if (!blocks.length) return;

    if (busyForms.has(form)) {
      stopEvent(event);
      return;
    }

    blocks.forEach(updateBlock);

    if (!form.checkValidity()) {
      stopEvent(event);
      form.reportValidity();
      return;
    }

    // ミニミニランドセル未選択なら、既存の購入処理を使う
    if (!blocks.some(isMiniSelected)) return;

    stopEvent(event);
    void addWithOptions(form, blocks);
  }

  // 対象フォームの購入ボタンだけを処理する
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('button, input');
    if (
      !(button instanceof HTMLButtonElement) &&
      !(button instanceof HTMLInputElement)
    ) return;

    if (button.type !== 'submit' || button.disabled) return;

    handlePurchase(event, button.form);
  }, true);

  // Enterキーによる送信にも対応
  document.addEventListener('submit', (event) => {
    if (event.target instanceof HTMLFormElement) {
      handlePurchase(event, event.target);
    }
  }, true);

  window.FFRemakeMiniOptions = {
    version: 2,
    initialize,
    updateBlock,
    isMiniSelected,
    validateEngraving
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialize(),
      { once: true }
    );
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });
})();